<template>
  <div class="modal-overlay" @click.self="$emit('close')" data-testid="login-modal">
    <div class="modal-card">
      <img src="/source/png/nftwerks-logo.png" class="modal-logo" alt="NFTWerks" />

      <h2 class="modal-title">Welcome</h2>
      <p class="modal-sub">Enter your username and password.</p>

      <form class="modal-form" @submit.prevent="handleSubmit">
        <div class="field">
          <label class="field__label">Username <span class="required">*</span></label>
          <input
            v-model="username"
            class="field__input"
            type="text"
            autocomplete="username"
            data-testid="username-input"
          />
        </div>
        <div class="field">
          <label class="field__label">Password <span class="required">*</span></label>
          <div class="field__input-wrap">
            <input
              v-model="password"
              class="field__input field__input--pw"
              :type="showPw ? 'text' : 'password'"
              autocomplete="current-password"
              data-testid="password-input"
            />
            <button type="button" class="pw-toggle" @click="showPw = !showPw" data-testid="pw-toggle">
              <svg v-if="showPw" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="20" height="20"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
              <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="20" height="20"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
            </button>
          </div>
          <span class="forgot-link">Forgot password?</span>
        </div>

        <p v-if="errorMsg" class="error-msg">{{ errorMsg }}</p>

        <button type="submit" class="btn-gradient btn-full" :disabled="loading" data-testid="signin-btn">
          {{ loading ? 'Signing in…' : 'Sign In' }}
        </button>
      </form>

      <p class="signup-link" v-if="!embed">
        Don't have an account? <span @click="goSignup" class="signup-link__cta" data-testid="signup-link">Sign up</span>
      </p>
    </div>
  </div>
</template>

<script>
import http from '@/api/base'
import { setCookie } from '@/assets/js/cookie'

export default {
  name: 'LoginModal',
  emits: ['close', 'success'],
  props: {
    // In embed/checkout-widget mode there's no signup link — it would navigate
    // the buyer out of the standalone checkout.
    embed: { type: Boolean, default: false },
  },
  data() {
    return {
      username: '',
      password: '',
      showPw: false,
      loading: false,
      errorMsg: '',
    }
  },
  methods: {
    async handleSubmit() {
      this.errorMsg = ''
      if (!this.username || !this.password) {
        this.errorMsg = 'Please enter username and password.'
        return
      }
      this.loading = true
      try {
        const res = await http.post('/user/login', { userName: this.username, password: this.password, userType: 0 })
        if (res && res.code === 200 && res.data && res.data.token) {
          setCookie('token', res.data.token)
          this.$emit('success')
        } else {
          this.errorMsg = (res && res.msg) || 'Login failed. Please try again.'
        }
      } catch (e) {
        this.errorMsg = 'Login failed. Please check your credentials.'
      } finally {
        this.loading = false
      }
    },
    goSignup() {
      this.$emit('close')
      this.jump_participant_signup({ redirect: this.$route.fullPath })
    },
  },
}
</script>

<style scoped lang="scss">
$text: #0a0a0a;
$muted: #6b7280;
$field-bg: #f3f4f6;
$gradient: linear-gradient(90deg, #e879b2 0%, #a855f7 100%);
$radius-pill: 44px;
$radius-card: 36px;
$font: 'Inter', sans-serif;

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow-y: auto;
  padding: 24px 16px;
  box-sizing: border-box;
  // StartHead (the public nav) uses z-index: 20221027, so this must clear that
  // or the modal paints underneath the nav bar instead of over it.
  z-index: 20221028;
}

.modal-card {
  background: #fff;
  border-radius: $radius-card;
  padding: 40px 48px;
  width: 480px;
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 48px);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  font-family: $font;
  box-sizing: border-box;
}

.modal-logo {
  height: 52px;
  width: auto;
  object-fit: contain;
  margin-bottom: 8px;
}

.modal-title {
  font-size: 32px;
  font-weight: 700;
  margin: 0;
  color: $text;
  text-align: center;
}

.modal-sub {
  font-size: 15px;
  color: $muted;
  margin: 0;
  text-align: center;
}

.modal-form {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px 0 16px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;

  &__label {
    font-size: 14px;
    font-weight: 500;
    color: $text;
  }

  &__input {
    background: $field-bg;
    border: none;
    border-radius: $radius-pill;
    height: 56px;
    padding: 0 24px;
    font-size: 15px;
    font-family: $font;
    width: 100%;
    outline: none;
    box-sizing: border-box;
    &--pw { padding-right: 56px; }
  }

  &__input-wrap { position: relative; }
}

.required { color: #e53e3e; margin-left: 2px; }

.pw-toggle {
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: $muted;
  display: flex;
  align-items: center;
  padding: 0;
}

.forgot-link {
  font-size: 14px;
  text-align: right;
  display: block;
  cursor: pointer;
  background: $gradient;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 500;
}

.error-msg { color: #e53e3e; font-size: 14px; margin: 0; }

.btn-gradient {
  background: $gradient;
  color: #fff;
  border: none;
  border-radius: $radius-pill;
  padding: 18px 24px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  font-family: $font;
  &.btn-full { width: 100%; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
}

.signup-link {
  font-size: 15px;
  color: $muted;
  margin: 0;

  .signup-link__cta {
    cursor: pointer;
    font-weight: 600;
    background: $gradient;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
}
</style>
